import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, input, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Modal } from 'flowbite';
import { Subscription } from 'rxjs';
import { APP } from 'src/app/config/app';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import { ModalQueryParam, NotificationTypes } from 'src/app/models/enums';
import { ModalUrlService, TripsService, UsersService, UtilsService } from 'src/app/services';
import { images } from 'src/images';

@Component({
  selector: 'app-user-bookings-modal',
  templateUrl: './user-bookings-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserBookingsModalComponent implements OnInit, OnDestroy {
  readonly id = input.required<string>();

  bookings = signal<IBookingDetails[]>([]);
  userId = signal<number | undefined>(undefined);
  showed = signal(false);
  modal = signal<Modal | undefined>(undefined);

  private refreshTimer?: number;
  private routerSubscription?: Subscription;

  constructor(
    private usersService: UsersService,
    private tripsService: TripsService,
    private utilsService: UtilsService,
    private router: Router,
    private modalUrlService: ModalUrlService,
  ) {}

  ngOnInit() {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && this.showed()) {
        this.modal()?.hide();
      }
    });
  }

  ngOnDestroy() {
    clearInterval(this.refreshTimer);
    this.routerSubscription?.unsubscribe();
  }

  async showViewBookingsModal(userId?: number) {
    if (!userId) return;
    this.userId.set(userId);
    await this.loadBookings(userId);
    clearInterval(this.refreshTimer);
    this.refreshTimer = window.setInterval(() => this.loadBookings(userId), APP.USER_BOOKINGS_REFRESH_MS);
    const modal = new Modal(document.getElementById(this.id()), {
      placement: 'center',
      closable: false,
      onHide: () => {
        clearInterval(this.refreshTimer);
        this.showed.set(false);
        this.modalUrlService.markClosed(ModalQueryParam.USER_BOOKINGS);
      },
      onShow: () => this.showed.set(true),
    });
    this.modal.set(modal);
    modal.toggle();
    this.modalUrlService.markOpen(ModalQueryParam.USER_BOOKINGS, String(userId));
  }

  async restoreFromUrl(expectedUserId?: number) {
    const restoredValue = await this.modalUrlService.consumeOpenValue(ModalQueryParam.USER_BOOKINGS);
    const restoredUserId = Number(restoredValue);
    if (!restoredValue || !Number.isInteger(restoredUserId) || restoredUserId <= 0) return;
    if (expectedUserId !== undefined && restoredUserId !== expectedUserId) return;
    await this.showViewBookingsModal(restoredUserId);
  }

  async validatePickUp(bookingId: number) {
    this.replaceBooking((await this.tripsService.validatePickUp(bookingId)) as IBookingDetails);
  }

  async validateDropOff(bookingId: number) {
    this.replaceBooking((await this.tripsService.validateDropOff(bookingId)) as IBookingDetails);
  }

  private async loadBookings(userId: number) {
    this.bookings.set(await this.usersService.getBookingsByUser(userId));
  }

  private replaceBooking(validated: IBookingDetails) {
    this.bookings.update((bookings) => bookings.map((booking) => (booking.id === validated.id ? validated : booking)));
    this.utilsService.showNotification(images.notifications.check, 'trips.actions.validate', NotificationTypes.SUCCESS);
  }
}
